// shareBonsai.js — share a bonsai with a friend via a self-contained link.
//
// Why this works with zero backend: a saved tree is tiny, fully-reproducible
// data — a seed + an edit-history log + the parameters (see
// gallery.captureTreeData / Tree.replayHistory). That's small enough (~1–2 KB of
// JSON) to live entirely inside a URL. We pack it into the URL **hash**
// (`#t=...`), which the browser never sends to a server, so the whole feature
// is client-side: no database, no endpoint, no privacy footprint. Opening the
// link decodes the payload and rebuilds the *exact* tree through the same path
// the gallery's Load uses.
//
// Separation of concerns: gallery.js owns "tree data <-> scene"
// (captureShareData / applySharedTree); this module owns only
// "tree data <-> shareable link" — encode/decode, clipboard, the native share
// sheet, and the boot-time check for an inbound link.

import { app, PARAMETER_DEFAULTS } from './core.js';
import { captureShareData, captureShareDataFor, applySharedTree, notify } from './gallery.js';
import { captureSceneToggles, applySceneToggles } from './settings.js';
import { playSfx } from './audio.js';

// Bump if the payload shape ever changes incompatibly; decode rejects versions
// it doesn't recognize (the recipient falls back to a fresh random tree).
const SHARE_VERSION = 1;
const HASH_KEY = 't';

// ---------------------------------------------------------------------------
// Encoding: JSON -> (optionally deflate-compressed) bytes -> base64url.
//
// deflate-raw shrinks the long links dramatically (a heavily-edited tree's
// `history` array is highly repetitive — ~70% smaller) while barely moving short
// ones. So we compress, then keep whichever is shorter (raw JSON wins on tiny
// payloads, where deflate's overhead isn't repaid). Decode just tries to inflate
// first and falls back to plain on failure, so there's no scheme marker and no
// version bump: older uncompressed links (and any browser lacking
// CompressionStream) decode through the plain path unchanged.
// ---------------------------------------------------------------------------

// CompressionStream is ~universal (Safari 16.4+, 2023) but feature-detected so an
// ancient browser still produces/reads a (plain) link rather than throwing.
const CAN_COMPRESS =
  typeof CompressionStream === 'function' && typeof DecompressionStream === 'function';

// Run bytes through a (de)compression stream and collect the result.
async function pipeThrough(bytes, stream) {
  const out = new Blob([bytes]).stream().pipeThrough(stream);
  return new Uint8Array(await new Response(out).arrayBuffer());
}
const deflate = (bytes) => pipeThrough(bytes, new CompressionStream('deflate-raw'));
const inflate = (bytes) => pipeThrough(bytes, new DecompressionStream('deflate-raw'));

// Uint8Array <-> URL-safe base64 (+/ -> -_, no = padding). btoa/atob work on
// latin1 strings, so we map bytes <-> char codes by hand (the UTF-8 step lives in
// TextEncoder/TextDecoder around the JSON, since the payload is binary here).
function bytesToB64url(bytes) {
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
function b64urlToBytes(str) {
  const bin = atob(str.replace(/-/g, '+').replace(/_/g, '/'));
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

async function encodePayload(obj) {
  const jsonBytes = new TextEncoder().encode(JSON.stringify(obj));
  const plain = bytesToB64url(jsonBytes);
  if (!CAN_COMPRESS) return plain;
  try {
    const packed = bytesToB64url(await deflate(jsonBytes));
    return packed.length < plain.length ? packed : plain; // only if it actually wins
  } catch {
    return plain;
  }
}

async function decodePayload(str) {
  const bytes = b64urlToBytes(str);
  if (CAN_COMPRESS) {
    try {
      // Inflate succeeds only on a genuinely compressed link; JSON.parse then
      // validates it. Anything else (plain or legacy) throws and falls through.
      return JSON.parse(new TextDecoder().decode(await inflate(bytes)));
    } catch {
      /* not a compressed payload — decode it as plain bytes below */
    }
  }
  return JSON.parse(new TextDecoder().decode(bytes));
}

// ---------------------------------------------------------------------------
// Payload slimming. A share link only needs the data that reproduces the tree,
// so before encoding we (1) drop the gallery's display-only fields
// (branchCount/mainBranchCount/hasMainBranches — replay never reads them),
// (2) diff `parameters` down to just what differs from the defaults, and
// (3) round floats to 3 decimals (lossless at our scales — radians, unit
// normals). The recipient re-merges the diff over the same defaults. Because a
// full parameters object merged over the defaults yields itself, this stays
// backward-compatible with older links that carried the whole set.
// ---------------------------------------------------------------------------

// 3 decimals: ≈0.001 rad (0.06°) on shape rotations, well below perceptible.
const round3 = (n) => (typeof n === 'number' ? Math.round(n * 1000) / 1000 : n);
const roundDeep = (v) => {
  if (typeof v === 'number') return round3(v);
  if (v && typeof v === 'object') {
    const out = Array.isArray(v) ? [] : {};
    for (const k in v) out[k] = roundDeep(v[k]);
    return out;
  }
  return v;
};

// Params that never change the *look* of a static shared tree, so we never carry
// them: windDirection only drives the live foliage sway (an animation uniform),
// and shapeMode is transient UI state. The recipient inherits the defaults.
const PARAM_SKIP = new Set(['windDirection', 'shapeMode']);

// Emit only the (rounded) parameters that differ from the defaults.
function diffParams(params) {
  const diff = {};
  for (const key in params) {
    if (PARAM_SKIP.has(key)) continue;
    const val = roundDeep(params[key]);
    if (JSON.stringify(val) !== JSON.stringify(roundDeep(PARAMETER_DEFAULTS[key]))) {
      diff[key] = val;
    }
  }
  return diff;
}

// Inverse of diffParams: rebuild the full parameter set from a diff.
function expandParams(diff) {
  return { ...PARAMETER_DEFAULTS, ...(diff || {}) };
}

// Strip a captured payload down to the reproducible essentials, rounding floats.
function compactShareData(shareData) {
  const out = { parameters: diffParams(shareData.parameters || {}) };
  if (shareData.seed != null) out.seed = shareData.seed;
  if (shareData.history && shareData.history.length) {
    out.history = shareData.history.map((e) =>
      e && e.op === 'shape' ? { ...e, ry: round3(e.ry), rz: round3(e.rz) } : e
    );
  }
  if (shareData.leanData) out.leanData = roundDeep(shareData.leanData);
  // Trunk dims the branches were sized against — only needed when they differ from
  // the final/display trunk (i.e. the trunk was reshaped after generation).
  // Otherwise the recipient rebuilds straight at the final dims. The per-grow trunc
  // stamps already ride along inside `history`.
  const g = shareData.genTrunc, p = shareData.parameters;
  if (g && p && (g.h !== p.truncHeight || g.t !== p.truncThickness)) {
    out.genTrunc = { h: round3(g.h), t: round3(g.t) };
  }
  if (shareData.name) out.name = shareData.name;
  return out;
}

// Build a share link from a ready tree payload (gallery's captureShareData /
// captureShareDataFor), composing in which decor is currently on.
async function buildShareURL(shareData) {
  const scene = captureSceneToggles();
  const payload = { v: SHARE_VERSION, ...compactShareData(shareData) };
  if (scene.length) payload.scene = scene; // omit when no decor is on
  const encoded = await encodePayload(payload);
  // origin+pathname drops any existing query/hash so the link stays clean.
  return `${location.origin}${location.pathname}#${HASH_KEY}=${encoded}`;
}

// ---------------------------------------------------------------------------
// Outbound delivery: hand a link off via the native share sheet on devices that
// support it (mobile), falling back to clipboard copy + toast everywhere else.
// ---------------------------------------------------------------------------
async function deliverShareURL(url) {
  if (navigator.share) {
    try {
      await navigator.share({
        title: 'My Bonsai',
        text: 'Check out the bonsai I grew 🌳',
        url
      });
      return;
    } catch (err) {
      if (err && err.name === 'AbortError') return; // user dismissed the sheet
      // any other failure: fall through to the clipboard path
    }
  }

  try {
    await navigator.clipboard.writeText(url);
    notify('🔗 Share link copied to clipboard!', 'linear-gradient(45deg, #16a085, #1abc9c)');
  } catch (err) {
    // Clipboard blocked (e.g. insecure context / permissions) — surface the
    // link so it can be copied by hand.
    window.prompt('Copy this link to share your bonsai:', url);
  }
}

// Share the tree currently on screen (the Share button in the control panel).
window.shareBonsai = async function() {
  if (!app.tree) {
    alert('No tree to share! Generate a tree first.');
    return;
  }
  playSfx('sparkle'); // share confirmation chime
  await deliverShareURL(await buildShareURL(captureShareData()));
};

// Share a specific saved gallery tree (the 🔗 on each card). Pulls that card's
// stored config straight from the gallery — no need to load it onto the scene.
window.shareGalleryTree = async function(treeId) {
  const shareData = captureShareDataFor(treeId);
  if (!shareData) {
    alert('Tree not found!');
    return;
  }
  playSfx('sparkle'); // share confirmation chime
  await deliverShareURL(await buildShareURL(shareData));
};

// ---------------------------------------------------------------------------
// Inbound: called once at boot. If the URL carries a shared tree, rebuild it
// and return true (so main.js skips the default random tree). The payload is
// then stripped from the address bar so a refresh doesn't re-trigger it and the
// URL reads clean.
// ---------------------------------------------------------------------------
export async function loadSharedTreeFromURL() {
  const raw = location.hash.startsWith('#') ? location.hash.slice(1) : location.hash;
  const encoded = new URLSearchParams(raw).get(HASH_KEY);
  if (!encoded) return false;

  let loaded = false;
  try {
    const payload = await decodePayload(encoded);
    if (!payload || payload.v !== SHARE_VERSION) {
      console.warn('Unrecognized share-link version:', payload && payload.v);
    } else {
      payload.parameters = expandParams(payload.parameters); // re-merge the diff over defaults
      applySharedTree(payload);
      applySceneToggles(payload.scene); // restore the sender's decor (off if absent)
      loaded = true;
    }
  } catch (err) {
    console.warn('Could not load shared bonsai from URL:', err);
  } finally {
    // Clean the address bar whether or not decode succeeded.
    history.replaceState(null, '', location.origin + location.pathname);
  }
  return loaded;
}
